import { logger } from '../../utils/logger';
import type { BrandMapping, DomainMatcher } from './branding.types';
import { DEFAULT_BRAND_ID, brands } from './brands';

/**
 * Domain-to-brand mapping configuration
 * Priority: Higher numbers take precedence (default: 0)
 */
export const brandMappings: BrandMapping[] = [
  // Production domains
  { matcher: 'platform.catalyzed.ai', brandId: 'catalyzed', priority: 10 },
  { matcher: 'catalyzed.ai', brandId: 'catalyzed', priority: 10 },
  { matcher: 'platform.us.catalyzed.ai', brandId: 'catalyzed', priority: 10 },

  // Development domains
  { matcher: 'localhost:5174', brandId: 'catalyzed', priority: 5 },
  { matcher: 'localhost:5173', brandId: 'catalyzed', priority: 5 },
  { matcher: '127.0.0.1:5174', brandId: 'catalyzed', priority: 5 },

  // Example client domains (subdomain pattern)
  {
    matcher: /^(.+)\.example-client\.com$/,
    brandId: 'example-client',
    priority: 8,
  },
  {
    matcher: 'demo.catalyzed.ai',
    brandId: 'example-client',
    priority: 8,
  },

  // Custom matcher function example
  {
    matcher: (hostname) => hostname.includes('purple'),
    brandId: 'purple-brand',
    priority: 7,
  },

  // Default fallback (lowest priority)
  { matcher: '*', brandId: DEFAULT_BRAND_ID, priority: -1 },
];

/**
 * Match a hostname against a domain matcher
 */
const matchDomain = (hostname: string, matcher: DomainMatcher): boolean => {
  if (typeof matcher === 'string') {
    return matcher === '*' || matcher === hostname;
  }
  if (matcher instanceof RegExp) {
    return matcher.test(hostname);
  }
  if (typeof matcher === 'function') {
    return matcher(hostname);
  }
  return false;
};

/**
 * Get the brand ID for a given hostname
 */
export const getBrandIdForDomain = (hostname: string): string => {
  // Sort by priority (higher first)
  const sortedMappings = [...brandMappings].sort(
    (a, b) => (b.priority || 0) - (a.priority || 0),
  );

  // Find the first matching mapping
  const matchedMapping = sortedMappings.find((mapping) =>
    matchDomain(hostname, mapping.matcher),
  );

  return matchedMapping?.brandId || DEFAULT_BRAND_ID;
};

/**
 * Get the current domain's brand ID
 */
export const getCurrentBrandId = (): string => {
  // Check for development override first
  if (process.env.NODE_ENV === 'development') {
    const override = window.localStorage.getItem('BRAND_OVERRIDE');
    if (override && brands[override]) {
      logger.debug(`Using brand override: ${override}`);
      return override;
    }
  }

  const hostname =
    window.location.hostname +
    (window.location.port ? `:${window.location.port}` : '');

  const brandId = getBrandIdForDomain(hostname);
  logger.debug(`Detected brand '${brandId}' for hostname '${hostname}'`);

  return brandId;
};

/**
 * Development utility: Override the current brand
 */
export const overrideBrand = (brandId: string | null): void => {
  if (process.env.NODE_ENV !== 'development') {
    logger.warn('Brand override is only available in development mode');
    return;
  }

  if (brandId === null) {
    window.localStorage.removeItem('BRAND_OVERRIDE');
    logger.info('Brand override cleared');
  } else if (brands[brandId]) {
    window.localStorage.setItem('BRAND_OVERRIDE', brandId);
    logger.info(`Brand override set to: ${brandId}`);
  } else {
    logger.error(`Unknown brand ID: ${brandId}`);
    logger.info('Available brands:', { brands: Object.keys(brands) });
  }

  // Reload to apply the new brand
  window.location.reload();
};

// Export for development console usage
if (process.env.NODE_ENV === 'development') {
  (window as unknown as { overrideBrand: typeof overrideBrand }).overrideBrand = overrideBrand;
  (window as unknown as { availableBrands: string[] }).availableBrands = Object.keys(brands);
  logger.info(
    'Brand override available. Use window.overrideBrand(brandId) to switch brands.',
  );
  logger.info('Available brands:', { brands: Object.keys(brands) });
}
